import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap } from "rxjs";
import { BaseRespone, PaginationResult } from "src/app/models/common.response";
import { environment } from "src/environments/environment";
import { PaginationRequest } from "src/app/models/common.request";
import { IUsers, UserAvailabilityPayload, UserAvalibility } from "./iusers";


@Injectable({ providedIn: 'root' })
export class UserService {
    private _user: BehaviorSubject<IUsers | null> = new BehaviorSubject<IUsers | null>(null);
    private _avalibility: BehaviorSubject<UserAvalibility | null> = new BehaviorSubject<UserAvalibility | null>(null);
    private _users: BehaviorSubject<IUsers[]> = new BehaviorSubject<IUsers[]>([]);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);

    constructor(
        private _httpClient: HttpClient
    ) { }

    get user$(): Observable<IUsers | null> {
        return this._user.asObservable();
    }

    get users$(): Observable<IUsers[]> {
        return this._users.asObservable();
    }

    get pagination$(): Observable<PaginationResult | null> {
        return this._pagination.asObservable();
    }


    getAllUsers(userdata: PaginationRequest): Observable<BaseRespone<{ pagination: PaginationResult; users: IUsers[] }>> {
        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; users: IUsers[] }>>(`${environment.baseURl}/User/list`, userdata).pipe(
            tap((response) => {
                this._pagination.next(response.result.pagination);
                this._users.next(response.result.users);
            })
        );
    }


    createUser(user: FormData): Observable<BaseRespone<IUsers>> {
        return this._httpClient.post<BaseRespone<IUsers>>(`${environment.baseURl}/User`, user)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newusers) => {
                    // Return the new users
                    return newusers;
                })
            )
    }

    updateUsers(user: FormData): Observable<BaseRespone<IUsers>> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.put<BaseRespone<IUsers>>(`${environment.baseURl}/User`, user).pipe(
                map((updated: BaseRespone<IUsers>) => {
                    const userid = user.get('id');
                    // Find the index of the updated product
                    const index = users.findIndex(item => item.id === Number(userid));

                    // Update the product
                    if (index !== -1) {
                        users[index] = { ...users[index], ...updated.result };
                        this._users.next(users);
                    }

                    // Return the updated product
                    return updated;
                })
            )), catchError((error) => {
                return of(error.error);
            })
        );
    }

    deleteUser(id: number): Observable<BaseRespone<boolean>> {
        return this.users$.pipe(
            take(1),
            switchMap(user =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseURl}/User/${id}`).pipe(
                    map((deletedResponse) => {
                        if (deletedResponse.result) {

                            const updateduser = user.filter(item => item.id !== id);
                            this._users.next(updateduser);
                        }
                        return deletedResponse;
                    })
                )
            ), catchError((error) => {
                return of(error.error);
            })
        );
    }

    createUserAvalibility(availability: UserAvailabilityPayload): Observable<BaseRespone<UserAvailabilityPayload>> {
        return this._httpClient.post<BaseRespone<UserAvailabilityPayload>>(`${environment.baseURl}/User/availability`, availability)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newusers) => {
                    // Return the new users
                    return newusers;
                })
            )
    }

    getUserAvalibility(id: number) {
        return this._httpClient.get<BaseRespone<UserAvalibility>>(`${environment.baseURl}/User/${id}/availability`).pipe(
            tap((response) => {
                this._avalibility.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to Fetching data:', error);
                throw error;
            })
        );
    }

    getUserByID(id: Number) {
        return this._httpClient.get<BaseRespone<IUsers>>(`${environment.baseURl}/User/${id}`).pipe(
            tap((response) => {
                this._user.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to Fetching data:', error);
                throw error;
            })
        );
    }

}
